// Upload PDF in the Record's words (spec C8, C16): what a file can be, the size line, and why an
// upload did not go through. Pure functions, so the dialog and the tests read the same lines.
import { maxUploadBytes, type UploadKind } from '../auth/api';

/** The choices in "What is it?", in this order; Renewal dec page first. */
export const uploadKinds: { kind: UploadKind; label: string }[] = [
  { kind: 'dec_page', label: 'Renewal dec page' },
  { kind: 'id_cards', label: 'ID cards' },
  { kind: 'invoice', label: 'Carrier invoice' },
  { kind: 'other', label: 'Other' },
];

export const uploadLabel = (kind: UploadKind) =>
  uploadKinds.find((k) => k.kind === kind)?.label ?? 'File';

/** "840 KB", "2.4 MB" */
export function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const tooBigText = 'This file is over 20 MB. Upload a smaller PDF.';
export const notPdfText = 'This file is not a PDF. Choose a PDF file.';

/**
 * Checked in the browser before the dialog opens; the server checks again (the bytes, not the
 * name, decide what a PDF is).
 */
export function pickProblem(file: { name: string; type: string; size: number }): string | null {
  if (file.size > maxUploadBytes) return tooBigText;
  const pdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!pdf) return notPdfText;
  if (file.size === 0) return 'This file is empty. Choose another PDF.';
  return null;
}

/** Why the upload did not go through, from the server's answer. */
export function uploadError(status: number, error: string): string {
  if (status === 413 || error === 'too_large') return tooBigText;
  if (status === 415 || error === 'not_pdf') return notPdfText;
  if (error === 'empty') return 'This file is empty. Choose another PDF.';
  if (status === 403) return 'You no longer work this record. Reload the page.';
  if (status === 404) return 'This record is gone. Reload the page.';
  if (status === 0) return 'No connection. Check the internet and try again.';
  return 'The upload did not go through. Try again.';
}

/** The message after an upload. */
export function uploadedText(kind: UploadKind, duplicate: boolean): string {
  if (duplicate) return 'This file was already on the record.';
  return `${uploadLabel(kind)} uploaded.`;
}
